import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { identitySchema, type IdentityValues, suggestVatId } from '@/lib/validators/onboarding';
import { useOnboardingStore } from '@/lib/stores/onboarding';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import { Card, CardContent } from '@/components/ui/card';

export const Route = createFileRoute('/onboarding/identity')({
  component: IdentityStep,
});

const LEGAL_FORM_OPTIONS = [
  { value: 'sp',    label: 's.p. (samostojni podjetnik)' },
  { value: 'doo',   label: 'd.o.o. (družba z omejeno odgovornostjo)' },
  { value: 'dd',    label: 'd.d. (delniška družba)' },
  { value: 'kd',    label: 'k.d. (komanditna družba)' },
  { value: 'other', label: 'Drugo' },
];

function IdentityStep() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { identity, setIdentity, setStep, recordGdprConsent } = useOnboardingStore();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<IdentityValues>({
    resolver: zodResolver(identitySchema),
    defaultValues: {
      davcna:             identity.davcna ?? '',
      legalName:          identity.legalName ?? '',
      tradeName:          identity.tradeName ?? '',
      legalForm:          identity.legalForm ?? 'sp',
      address:            identity.address ?? '',
      city:               identity.city ?? '',
      postalCode:         identity.postalCode ?? '',
      vatId:              identity.vatId ?? '',
      registrationNumber: identity.registrationNumber ?? '',
      gdprConsent:        false as unknown as true,
    },
    mode: 'onChange',
  });

  const davcna = watch('davcna');
  const gdprConsent = watch('gdprConsent');

  // Auto-suggest DDV ID
  useEffect(() => {
    const suggested = suggestVatId(davcna ?? '');
    if (suggested) setValue('vatId', suggested, { shouldValidate: false });
  }, [davcna, setValue]);

  function onSubmit(data: IdentityValues) {
    setIdentity(data);
    setStep('banking');
    recordGdprConsent();
    void navigate({ to: '/onboarding/banking' });
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-6">
          <h2 className="font-display text-display-sm text-forge">{t('identity.heading')}</h2>
          <p className="text-sm text-steel mt-1">{t('identity.subheading')}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          {/* Row: Davčna */}
          <Field
            id="davcna"
            label={t('identity.fields.davcna')}
            hint={t('identity.fields.davcna_hint')}
            error={errors.davcna?.message}
            required
          >
            <Input
              id="davcna"
              inputMode="numeric"
              maxLength={8}
              placeholder="12345678"
              error={errors.davcna?.message}
              {...register('davcna')}
            />
          </Field>

          {/* Row: Uradni naziv + pravna oblika */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field
              id="legalName"
              label={t('identity.fields.legal_name')}
              hint={t('identity.fields.legal_name_hint')}
              error={errors.legalName?.message}
              required
              className="sm:col-span-2"
            >
              <Input
                id="legalName"
                placeholder="Janez Novak s.p."
                error={errors.legalName?.message}
                {...register('legalName')}
              />
            </Field>

            <Field
              id="legalForm"
              label={t('identity.fields.legal_form')}
              error={errors.legalForm?.message}
              required
            >
              <Controller
                name="legalForm"
                control={control}
                render={({ field }) => (
                  <Select
                    id="legalForm"
                    options={LEGAL_FORM_OPTIONS}
                    error={errors.legalForm?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Field>
          </div>

          {/* Ime za stranke */}
          <Field
            id="tradeName"
            label={t('identity.fields.trade_name')}
            hint={t('identity.fields.trade_name_hint')}
            error={errors.tradeName?.message}
            required
          >
            <Input
              id="tradeName"
              placeholder="Janez Hišnik"
              error={errors.tradeName?.message}
              {...register('tradeName')}
            />
          </Field>

          {/* Naslov */}
          <Field
            id="address"
            label={t('identity.fields.address')}
            hint={t('identity.fields.address_hint')}
            error={errors.address?.message}
            required
          >
            <Input
              id="address"
              placeholder="Trubarjeva ulica 12"
              error={errors.address?.message}
              {...register('address')}
            />
          </Field>

          {/* Kraj + poštna */}
          <div className="grid grid-cols-2 gap-4">
            <Field
              id="postalCode"
              label={t('identity.fields.postal_code')}
              error={errors.postalCode?.message}
              required
            >
              <Input
                id="postalCode"
                inputMode="numeric"
                maxLength={4}
                placeholder="6000"
                error={errors.postalCode?.message}
                {...register('postalCode')}
              />
            </Field>

            <Field
              id="city"
              label={t('identity.fields.city')}
              error={errors.city?.message}
              required
            >
              <Input
                id="city"
                placeholder="Koper"
                error={errors.city?.message}
                {...register('city')}
              />
            </Field>
          </div>

          {/* DDV ID + matična */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="vatId"
              label={t('identity.fields.vat_id')}
              hint={t('identity.fields.vat_id_hint')}
              error={errors.vatId?.message}
            >
              <Input
                id="vatId"
                placeholder="SI12345678"
                error={errors.vatId?.message}
                {...register('vatId')}
              />
            </Field>

            <Field
              id="registrationNumber"
              label={t('identity.fields.registration_number')}
              hint={t('identity.fields.registration_number_hint')}
              error={errors.registrationNumber?.message}
            >
              <Input
                id="registrationNumber"
                inputMode="numeric"
                maxLength={10}
                placeholder="1234567890"
                error={errors.registrationNumber?.message}
                {...register('registrationNumber')}
              />
            </Field>
          </div>

          {/* GDPR consent — NIKOLI pre-checked */}
          <div className="mt-6 pt-4 border-t border-linen">
            <Controller
              name="gdprConsent"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="gdprConsent"
                  checked={field.value === true}
                  onChange={(e) => field.onChange(e.target.checked || undefined)}
                  error={errors.gdprConsent?.message}
                  label={
                    <span>
                      {t('identity.gdpr.label')}{' '}
                      <Link
                        to="/pravno/$slug"
                        params={{ slug: 'pogoji' }}
                        className="text-copper underline underline-offset-2"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('identity.gdpr.terms_link')}
                      </Link>
                      {' '}{t('identity.gdpr.and')}{' '}
                      <Link
                        to="/pravno/$slug"
                        params={{ slug: 'zasebnost' }}
                        className="text-copper underline underline-offset-2"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('identity.gdpr.privacy_link')}
                      </Link>
                      {' '}{t('identity.gdpr.and2')}{' '}
                      <Link
                        to="/pravno/$slug"
                        params={{ slug: 'dpa' }}
                        className="text-copper underline underline-offset-2"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('identity.gdpr.dpa_link')}
                      </Link>
                      .
                    </span>
                  }
                />
              )}
            />
            {!gdprConsent && (
              <p className="text-xs text-steel mt-2 ml-6">
                {t('identity.gdpr.required_hint')}
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={!isValid || !gdprConsent}
              aria-disabled={!isValid || !gdprConsent}
            >
              {t('actions.next', { ns: 'common' })}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// Named export za teste — brez Route wrapping-a
export { IdentityStep as IdentityStepUnwrapped };
